import { Node, mergeAttributes } from '@tiptap/core';

// Helper function to extract video ID from YouTube URL
const extractVideoId = (url) => {
  if (!url) return null;
  
  // Remove any whitespace
  url = url.trim();
  
  // Handle different YouTube URL formats
  let videoId = null;
  
  // Format: https://www.youtube.com/watch?v=VIDEO_ID
  // Format: https://youtube.com/watch?v=VIDEO_ID
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  if (watchMatch && watchMatch[1]) {
    videoId = watchMatch[1].substring(0, 11); // YouTube video IDs are always 11 characters
  }
  
  // Format: https://www.youtube.com/v/VIDEO_ID
  const vMatch = url.match(/youtube\.com\/v\/([^&\n?#]+)/);
  if (vMatch && vMatch[1]) {
    videoId = vMatch[1].substring(0, 11);
  }
  
  // If we found a video ID, validate it's 11 characters (standard YouTube ID length)
  if (videoId && videoId.length === 11) {
    return videoId;
  }
  
  return null;
};

// Export the helper function for use in components
export { extractVideoId };

export const YouTube = Node.create({
  name: 'youtube',

  addOptions() {
    return {
      inline: false,
      allowFullscreen: true,
      HTMLAttributes: {},
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: 640,
      },
      height: {
        default: 360,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-youtube-video]',
        getAttrs: (node) => {
          const iframe = node.querySelector('iframe');
          if (iframe && iframe.src) {
            // Extract video ID from the iframe src
            const videoId = extractVideoId(iframe.src);
            if (videoId) {
              // Store the original URL (will be normalized in renderHTML)
              return {
                src: iframe.src, // This will be normalized to embed URL in renderHTML
                width: iframe.width || 640,
                height: iframe.height || 360,
              };
            }
          }
          // If no valid video ID found, don't parse this node
          return false;
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Always extract video ID from the src attribute
    const videoId = extractVideoId(HTMLAttributes.src);
    
    if (!videoId) {
      // If no valid video ID, show error message
      return ['div', { 'data-youtube-video': true, class: 'youtube-embed-wrapper' }, ['p', { style: 'color: red; padding: 1em;' }, 'Invalid YouTube URL. Please use a valid YouTube video URL.']];
    }

    // Always use the embed URL format
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;

    return [
      'div',
      { 'data-youtube-video': true, class: 'youtube-embed-wrapper' },
      [
        'iframe',
        mergeAttributes(
          {
            width: HTMLAttributes.width || this.options.width || 640,
            height: HTMLAttributes.height || this.options.height || 360,
            src: embedUrl,
            frameborder: '0',
            allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
            allowfullscreen: this.options.allowFullscreen !== false,
          },
          this.options.HTMLAttributes,
          HTMLAttributes
        ),
      ],
    ];
  },

  addCommands() {
    return {
      setYouTubeVideo: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
});

